import {
	gtmPush,
} from './common-utils.js';

/**
 * Enable the submit button on a form step after validating each field.
 * @module validate-form
 * @author Vincent Falconi <vfalconi@sumologic.com>
 */
import validatePasswordField from './validate-password.js';



/**
 * Use client validation and check that value isn't empty space
 * @param {element} el
 */
const checkOpenFieldValidity = (el) => {
	const { value } = el;

	return (el.checkValidity() && value.trim() !== '' && value.length <= 100)
}



/**
 * Check entire form for valid inputs on required
 * fields, and whether they meet pattern requirements
 * @param {element} form
 */
const getFormValidity = (form) => {
	const inputs = form.querySelectorAll(':required');
	const results = new Map();

	if (form.dataset.extValidation !== undefined) {
		results.set('external', (form.dataset?.extValid === 'pass'))
	}

	Array.from(inputs).forEach(el => {
		let validationResults;

		if (el.name === 'Password') {
			validationResults = validatePasswordField(el.value);
		} else {
			validationResults = checkOpenFieldValidity(el);
		}

		results.set(el.name, validationResults);
	});

	return results;
}



/**
 * Check validity results
 * @param {map} results
 */
const checkValidity = (results) => {
	return results.values().map((result => {
		if (result.constructor.name === 'Map') {
			return checkValidity(result);
		}

		return result
	})).every(p => p === true);
}



/**
 * Toggle inline validation policy indicators
 * (The little dots under the password field)
 * @param {bool} result
 * @param {string} policyName
 */
const togglePasswordIndicators = (result, policyName) => {
	const indicator = document.querySelector(`[data-validation="${policyName}"]`);
	const addState = (result === true ? 'passed' : 'failed');
	const removeState = (result === true ? 'failed' : 'passed');

	if (indicator) {
		indicator.classList.add(`validation-policy--${addState}`);
		indicator.classList.remove(`validation-policy--${removeState}`);
	}
}



/**
 * Update password validation indicators
 * @param {map} results
 */
const updatePasswordValidation = (results) => {
	results.forEach((result, policyName) => {
		togglePasswordIndicators(result, policyName);
	});
}




/**
 * Toggle state/province field's `required` attribute.
 *
 * NOTE: this field is made visible with CSS based on whether the
 * US or Canada options are selected.
 * @param {element} selectedOption
 */
const toggleStateField = (selectedOption) => {
	const stateMenu = document.querySelector('#state');
	const provinceMenu = document.querySelector('#province');
	const optValue = selectedOption.value;

	switch (true) {
		case (optValue === 'United States'):
			provinceMenu.removeAttribute('required');
			stateMenu.setAttribute('required', 'required');
			break;
		case (optValue === 'Canada'):
			provinceMenu.setAttribute('required', 'required');
			stateMenu.removeAttribute('required');
			break;
		case (optValue === 'United States' && optValue === 'Canada'):
			provinceMenu.removeAttribute('required');
			stateMenu.removeAttribute('required');
			break;
	}
}



export const toggleAdditionalOptIn = (selectedOption) => {
	const additionalOptInFields = document.querySelectorAll('.slf__additional-optIn');
	const optInVisualState = (selectedOption.value === 'South Korea');

	[...additionalOptInFields].forEach((optInField) => {
		const inputs = optInField.querySelectorAll('input');

		switch (true) {
			case (optInVisualState === true):
				optInField.removeAttribute('inert');
				[...inputs].forEach((input) => {
					input.setAttribute('required', 'required');
				});
				break;
			case (optInVisualState === false):
				optInField.setAttribute('inert', 'inert');
				[...inputs].forEach((input) => {
					input.removeAttribute('required');
				});
				break;
		}
	});
}



/**
 * @param {element} form
 * @param {bool} enabled
 */
export const toggleButtonState = (form, enabled) => {
	const button = form.querySelector('button[type="Submit"]');

	if (enabled === true) {
		button.removeAttribute('disabled');
	} else {
		button.setAttribute('disabled', '');
	}
}



/**
 * @param {element} field
 */
const toggleInlineValidityMessage = (field, state) => {
	field.classList.toggle('slf__input-invalid', state);
}



/**
 * @param {element} form
 */
export const validateForm = (form, trigger = false) => {
	const formValidity = getFormValidity(form);
	const result = checkValidity(formValidity);

	if (trigger) {
		switch (true) {
			case (trigger.name === 'Country'):
				toggleStateField(trigger);
				toggleAdditionalOptIn(trigger);
				break;
			case (trigger.name === 'Password'):
				updatePasswordValidation(formValidity.get('Password'))
				break;
		}

		if (formValidity.get(trigger.name) === true) {
			toggleInlineValidityMessage(trigger, false);
		}
	}

	toggleButtonState(form, result);
}



/**
 * Validate form immediately after a field changes and,
 * if valid, enable the submit button.
 * @param {event} event
 */
const handleChange = (event) => {
	const el = event.target;
	const targetForm = el.closest('form');
	validateForm(targetForm, el);
}



/**
 * Show inline validation messages on blur rather than change
 * or keyup. Smoother UX.
 * @param {event} event
 */
const handleBlur = (event) => {
	const el = event.target;
	const state = !checkOpenFieldValidity(el);
	toggleInlineValidityMessage(el, state);

	if (state === true) {
		gtmPush('sumo_trial_error', {
			slfErrorType: 'validation',
			slfErrorDetails: el.name,
		});
	}
}



/**
 * Takes the string value of the maxlength attribute and
 * returns either an int or false
 * @param {string} strMaxLength
 */
const getMaxLength = (strMaxLength) => {
	const maxlength = parseInt(strMaxLength, 10);

	if (strMaxLength === null) {
		return false;
	}

	return maxlength;
}



/**
 * Attaches event listeners for form validation.
 * @name init
 */
export default () => {
	const inputs = document.querySelectorAll('.slf-step input:not([type="hidden"]),.slf-step select');
	const inputEvents = [
		'keyup',
		'change',
	]

	Array.from(inputs).forEach(el => {
		inputEvents.forEach(ev => {
			el.addEventListener(ev, handleChange);
		});

		el.addEventListener('blur', handleBlur);
	});
}
